/** Review choices are source-bound and persisted separately from the finalized report. */
import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { AlertTriangle, X } from 'lucide-react';
import { auth } from '../../../firebase';
import { Button } from '../../ui/button';
import type { SiteRagReport } from '../../../types/siteRag';
import type { CseReportReviewContext, CseReportConflict } from '../../../types/cseReportReview';
import { CSE_CONFLICT_EXPLAINER, cseConflictKey } from '../../../lib/cseConflicts';
import { canonicalJson } from '../../../lib/reportGeneration/reportContractHash';
import { loadCseReportReviewContext } from '../../../services/cseReportReview';
import { useStore } from '../../../store';

export interface CseConflictResolveDialogProps {
  report: SiteRagReport;
  open: boolean;
  onClose: () => void;
  onOpenSiteEvidence?: (opts?: { filterReportId?: string; focus?: 'conflicts' }) => void;
}
export const CseConflictResolveDialog: React.FC<CseConflictResolveDialogProps> = ({ report, open, onClose, onOpenSiteEvidence }) => {
  const saveResolution = useStore(s => s.saveCseConflictResolution);
  const [actorUid, setActorUid] = useState(auth.currentUser?.uid ?? null);
  const ownerUid = report._ownerUid ?? actorUid;
  const scope = canonicalJson([open, actorUid, ownerUid, report.id, report.siteId, report.sedId,
    report.sedVersionUsed, report.createdAt, report.updatedAt, report.qualityWarnings, report.claims]);
  const scopeRef = useRef(scope); scopeRef.current = scope;
  const [context, setContext] = useState<CseReportReviewContext | null>(null);
  const [contextScope, setContextScope] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const operation = useRef<{ draft: string; id: string } | null>(null);
  useEffect(() => onAuthStateChanged(auth, user => setActorUid(user?.uid ?? null)), []);
  useEffect(() => {
    const controller = new AbortController(); request.current = controller;
    setContext(null); setContextScope(null); setChoices({}); setError(null); setSaved(false); setSaving(false); operation.current = null;
    if (!open || !actorUid || !ownerUid) { setLoading(false); return () => controller.abort(); }
    setLoading(true);
    const active = () => !controller.signal.aborted && scopeRef.current === scope;
    void loadCseReportReviewContext(report.id, ownerUid, controller.signal).then(value => {
      if (!active()) return;
      if (value.basis.siteId !== (report.siteId ?? null)) throw new Error('The persisted report source no longer matches this view. Reload before reviewing.');
      const initial = Object.fromEntries(value.conflicts.map(row => [cseConflictKey(row.reportSectionId, row.field), '']));
      // A prior acknowledged review may be reopened without altering the original warning.
      if (value.review) for (const row of value.review.choices) initial[row.conflictKey] = row.value;
      setChoices(initial); setContext(value); setContextScope(scope); setSaved(Boolean(value.review));
    }).catch(cause => { if (active()) setError(cause instanceof Error ? cause.message : 'The saved report review could not be loaded.'); })
      .finally(() => { if (active()) setLoading(false); });
    return () => { controller.abort(); if (request.current === controller) request.current = null; };
  }, [scope, open, actorUid, ownerUid, report.id, report.siteId]);
  if (!open) return null;
  const selected = contextScope === scope ? context : null;
  const entries = selected?.conflicts ?? [];
  const allChosen = entries.length > 0 && entries.every(row => row.values.includes(choices[cseConflictKey(row.reportSectionId, row.field)] ?? ''));
  const handleSave = async () => {
    const controller = request.current;
    if (!selected || !allChosen || !controller || controller.signal.aborted || saving) return;
    const draft = canonicalJson([selected.basis, choices]);
    if (!operation.current || operation.current.draft !== draft) operation.current = { draft, id: crypto.randomUUID() };
    const operationId = operation.current.id;
    setSaving(true); setError(null);
    const active = () => !controller.signal.aborted && scopeRef.current === scope;
    try {
      await saveResolution(report.id, choices, { basis: selected.basis, operationId, signal: controller.signal });
      if (active()) { setSaved(true); operation.current = null; }
    } catch (cause) {
      if (active()) setError(cause instanceof Error ? cause.message : 'The report review could not be confirmed. Reload before retrying.');
    } finally { if (active()) setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/50" onClick={onClose} data-testid="cse-conflict-resolve-dialog">
      <div role="dialog" aria-modal="true" aria-labelledby="cse-review-title"
        className="bg-background w-[calc(100vw-2rem)] max-w-[640px] max-h-[85vh] overflow-y-auto shadow-2xl border border-border" onClick={event => event.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="min-w-0">
              <h2 id="cse-review-title" className="text-sm font-bold uppercase tracking-widest text-foreground">Review report conflicts</h2>
              <p className="text-[10px] text-text-secondary mt-1 leading-relaxed">{CSE_CONFLICT_EXPLAINER}</p>
            </div>
          </div>
          <button type="button" aria-label="Close conflict review" onClick={onClose} className="text-text-muted hover:text-text-primary shrink-0"><X className="w-4 h-4" aria-hidden /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="type-meta text-text-secondary">Reviewing saved report {report.id}. These choices do not change its original claims or warning.</p>
          <p className="type-meta text-text-secondary" data-testid="cse-sed-sync-pending">Correct the values in Screening facts, then choose Refresh and rebuild in Site Evidence. Regenerate this report to use the updated evidence.</p>
          {!actorUid && <p role="alert" className="type-meta text-danger">Sign in to review this report.</p>}
          {loading && <p role="status" className="type-meta text-text-secondary">Loading the saved report source…</p>}
          {entries.map(entry => {
            const key = cseConflictKey(entry.reportSectionId, entry.field);
            return <ConflictRow key={key} entry={entry} chosen={choices[key] ?? ''} disabled={saving}
              onChoose={value => { setSaved(false); operation.current = null; setChoices(prior => ({ ...prior, [key]: value })); }} />;
          })}
          {saved && selected && <p role="status" className="type-meta text-success">Review saved separately. Correct the source evidence and regenerate a new report; this finalized report stays unchanged.</p>}
          <div className="pt-2 border-t border-border-subtle space-y-2">
            {report.siteId && onOpenSiteEvidence && <button type="button" className="block text-[10px] text-primary hover:underline"
              onClick={() => { onOpenSiteEvidence({ filterReportId: report.id, focus: 'conflicts' }); onClose(); }}>Open Site Evidence to compare and correct sources →</button>}
          </div>
          {error && <p className="type-meta text-danger" role="alert">{error}</p>}
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>{saved ? 'Close' : 'Cancel'}</Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!selected || !allChosen || saving || saved}>{saving ? 'Saving…' : 'Save review'}</Button>
        </div>
      </div>
    </div>
  );
};
const ConflictRow: React.FC<{ entry: CseReportConflict; chosen: string; disabled: boolean; onChoose: (value: string) => void }> = ({ entry, chosen, disabled, onChoose }) => (
  <fieldset disabled={disabled} className="border border-amber-200/60 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 p-3 space-y-2">
    <legend className="text-[10px] font-mono text-text-primary">{entry.reportSectionId} · {entry.field}</legend>
    <p className="type-meta text-text-secondary">Record the value supported by the evidence:</p>
    <div className="flex flex-col gap-1.5">{entry.values.map(value => <label key={value} className="flex items-center gap-2 text-[11px] cursor-pointer">
      <input type="radio" name={`cse-${entry.reportSectionId}-${entry.field}`} checked={chosen === value} onChange={() => onChoose(value)} />
      <span className="font-mono">{value}</span>
    </label>)}</div>
  </fieldset>
);
export default CseConflictResolveDialog;
